import * as React from 'react';
import {useDispatch} from 'react-redux';

// @ts-expect-error
import type {FieldProps} from 'redux-form';

import {getHumanReadableEntityTypeFromGraphQLEnum} from 'nutshell-core/utils';

import {getIconDetails} from 'shells/connected-forms/helpers';
import type {CreateCustomFieldResponse} from 'shells/create-custom-field/types';
import {Line} from 'shells/divider/line';
import {FormField} from 'shells/form/form-field';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {ListRowWithIcon} from 'shells/list-row/list-row-with-icon';
import {TwoLineOptionCard} from 'shells/option-card/two-line-option-card';
import {SelectPickerSearch} from 'shells/select-picker-search/select-picker-search';

import type {FormFieldType} from 'nutshell-graphql-types';
import type {WfFormFragment_publishedVersion_fields as Field} from 'nutshell-graphql-types';

import type {FormFieldSchema, FormSchemaProperty} from '../../core/nutshell-fields/types';
import {fetchSchemaAction} from '../../forms/forms-actions';

import {CreateCustomFieldSidebar} from './create-custom-field-sidebar';
import {
    getFilteredFields,
    translateEntityToApiEntityType,
    renderIcon,
    isBaseField,
    getDisabledText,
} from './helpers';
import type {SelectedField} from './types';

import styles from './mapping-form.module.css';

const NEW_CUSTOM_FIELD_OPTION_VALUE = 'newCustomField';

type Props = FieldProps & {
    fieldType: FormFieldType;
    formSchema: FormFieldSchema;
    isIgnored: boolean;
    otherFields: Field[];
};

export function SelectedFieldField(props: Props): React.ReactElement<any> {
    const [isCreatingField, setIsCreatingField] = React.useState<boolean>(false);
    const [searchString, setSearchString] = React.useState<string>('');
    const [newCustomField, setNewCustomField] = React.useState<SelectedField | null | undefined>(
        null
    );

    const dispatch = useDispatch();

    const onChange = React.useCallback(
        (input: SelectedField) => {
            props.input.onChange(input);
        },
        [props.input]
    );

    React.useEffect(() => {
        if (newCustomField) {
            onChange(newCustomField);
            setNewCustomField(null);
        }
    }, [newCustomField, onChange]);

    const createCustomFieldOption = {
        label: '+ Create new custom field',
        value: NEW_CUSTOM_FIELD_OPTION_VALUE,
    } as const;

    const availableFields = getFilteredFields(
        props.formSchema,
        props.otherFields.filter(
            // Filter down to just fields that have been mapped
            // @ts-expect-error
            (field) => !field.needsMapping && field.entityName && field.fieldName
        ),
        props.fieldType
    );

    const availableFieldOptions = availableFields.map((field) => {
        // Some fields have an `options` property - unfortunately, for react-select,
        // an option with an `options` indicates that it is a `group` of options. There
        // is currently no way to disable this behavior within the select itself, the
        // best workaround is to filter that keyword off before passing it.
        // See: https://github.com/JedWatson/react-select/issues/3706
        const updatedField = {...field} as const;
        // @ts-expect-error
        delete updatedField.options;

        return updatedField;
    });

    // All options include create custom option
    // @ts-expect-error
    const fieldOptions = [createCustomFieldOption].concat(availableFieldOptions);

    const isFieldSelected = (field: FormSchemaProperty): boolean => {
        return Boolean(
            (field.name === props.input.value.fieldName ||
                field.attributeId === props.input.value.attributeId) &&
                translateEntityToApiEntityType(field.entityType) === props.input.value.entityName
        );
    };

    const renderField = (field: any, otherFields: any[]) => {
        const iconDetails = getIconDetails(translateEntityToApiEntityType(field.entityType));
        const icon = renderIcon(iconDetails.icon, 16, iconDetails.color);
        const entityName = translateEntityToApiEntityType(field.entityType);
        const isSelected = isFieldSelected(field);
        const disabledText = getDisabledText(otherFields, field.name, field.entityType);

        return (
            <TwoLineOptionCard
                key={`${entityName}-${field.name}`}
                title={field.label}
                subtitle={getHumanReadableEntityTypeFromGraphQLEnum(entityName)}
                icon={icon}
                onClick={() => {
                    onChange({
                        entityName,
                        fieldName: !field.attributeId ? field.name : null,
                        attributeId: field.attributeId ? field.attributeId : null,
                    });
                }}
                isSelected={isSelected}
                isDisabled={props.isIgnored || field.isDisabled}
                disabledPopoverText={field.isDisabled ? disabledText : ''}
            />
        );
    };

    // We want to prevent selected base fields (which appear as cards below the select) from also
    // being displayed in the select because it will already be indicated as selected by the card.
    let selectValue = availableFields
        .filter((field) => !isBaseField(field))
        .find((field) => isFieldSelected(field));

    // Empty select value needs to be null, not undefined, otherwise the select will not properly
    // clear the value if it is removed. Since Array.find() returns undefined if the field cannot
    // be found, we need to sub that for null.
    if (!selectValue) {
        // @ts-expect-error
        selectValue = null;
    }

    // Include 'create' option when searching
    const filterSelectOption = (
        option: {
            label: string;
            value: any;
            data: any;
        },
        filterText: string
    ) => {
        const lowerCaseFilter = filterText.toLowerCase();

        // Always include the 'create new custom field' option
        if (option.value === NEW_CUSTOM_FIELD_OPTION_VALUE) {
            return true;
        }

        // Check if the label includes the filter text
        const hasMatchingLabel = option.label.toLowerCase().includes(lowerCaseFilter);

        // Check if any tags include the filter text, if tags are defined
        const {tags} = option.data;
        if (!tags) {
            return hasMatchingLabel;
        }
        // @ts-expect-error
        const hasMatchingTag = tags.some((tag) => tag.toLowerCase().includes(lowerCaseFilter));

        // Return true if either the label or tags match the filter text
        return hasMatchingLabel || hasMatchingTag;
    };

    if (isCreatingField) {
        return (
            <CreateCustomFieldSidebar
                customFieldName={searchString}
                customFieldType='STRING'
                onCreateSuccess={(response: CreateCustomFieldResponse) => {
                    // @ts-expect-error
                    dispatch(fetchSchemaAction());
                    const newField = response.customField;
                    // Assuming we successfully get back a new custom field,
                    // we call onChange immediately, so that the user
                    // doesn't need to re-select the field they just created
                    if (newField) {
                        setNewCustomField({
                            entityName: translateEntityToApiEntityType(response.entity),
                            fieldName: null,
                            attributeId: newField.id,
                        });
                    }

                    setIsCreatingField(false);
                    setSearchString('');
                }}
                onClose={() => {
                    setIsCreatingField(false);
                    setSearchString('');
                }}
            />
        );
    }

    return (
        <FormField innerId={props.input.name}>
            <StackLegacy spacing={8}>
                <SelectPickerSearch
                    placeholder='Search all text fields…'
                    value={selectValue}
                    // @ts-expect-error
                    onChange={(selectedField) => {
                        if (!Array.isArray(selectedField) && selectedField) {
                            if (selectedField.value === NEW_CUSTOM_FIELD_OPTION_VALUE) {
                                setIsCreatingField(true);
                            } else {
                                onChange({
                                    entityName: translateEntityToApiEntityType(
                                        selectedField.entityType
                                    ),
                                    fieldName: !selectedField.attributeId
                                        ? selectedField.name
                                        : null,
                                    attributeId: selectedField.attributeId
                                        ? selectedField.attributeId
                                        : null,
                                });
                            }
                        }
                    }}
                    onInputChange={(input: string) =>
                        input.length ? setSearchString(input) : undefined
                    }
                    options={fieldOptions}
                    // @ts-expect-error
                    getOptionValue={(option) =>
                        option.value === NEW_CUSTOM_FIELD_OPTION_VALUE
                            ? option.value
                            : `${option.entityType}-${option.name}`
                    }
                    // @ts-expect-error
                    formatOptionLabel={(option) => {
                        if (option.value === NEW_CUSTOM_FIELD_OPTION_VALUE) {
                            return <div>{option.label}</div>;
                        }

                        return (
                            <ListRowWithIcon
                                title={option.label}
                                subtitle={getHumanReadableEntityTypeFromGraphQLEnum(
                                    translateEntityToApiEntityType(option.entityType)
                                )}
                                icon={
                                    getIconDetails(
                                        translateEntityToApiEntityType(option.entityType)
                                    ).icon
                                }
                                isDisabled={option.isDisabled}
                                disabledSubtitleText={getDisabledText(
                                    props.otherFields,
                                    option.name,
                                    option.entityType
                                )}
                            />
                        );
                    }}
                    filterOption={filterSelectOption}
                    isDisabled={props.isIgnored}
                />
                <Line text='OR' isDisabled={props.isIgnored} />
                <div className={styles['available-fields']}>
                    {availableFields
                        .filter((field) => isBaseField(field, 'CONTACT'))
                        .map((field) => renderField(field, props.otherFields))}
                </div>
                <div className={styles['available-fields']}>
                    {availableFields
                        .filter((field) => isBaseField(field, 'ACCOUNT'))
                        .map((field) => renderField(field, props.otherFields))}
                </div>
            </StackLegacy>
        </FormField>
    );
}
